import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FlexCol } from '@/components/ui/flex';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { MarkdownText } from '@/shared/MarkdownText';
import { UPDATE_TYPE_LABELS } from '@/pages/RegulatoryWatch/labels';
import {
  useAdminDeleteUpdateFileService,
  useAdminDiscardUpdateService,
  useAdminGenerateRedlineService,
  useAdminGenerateSummaryService,
  useAdminUpdateDetailService,
  useAdminUpdateUpdateService,
  useAdminUploadUpdateFileService,
  useAdminVersionsService,
} from '@/services/backoffice';
import { cn } from '@/utils/classUtils';
import type { AdminUpdateDetailResponse } from '@ditto/shared';
import { UPDATE_TYPES } from '@ditto/shared';
import dayjs from 'dayjs';
import {
  AlertCircle,
  ArrowLeft,
  Ban,
  Check,
  Copy,
  Download,
  FileText,
  Loader2,
  RefreshCw,
  Sparkles,
  Star,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type FC } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ADMIN_PATH, adminPaths, adminRegulatoryWatchPaths } from '@/paths';
import { DatePickerInput } from '../../components/RegulatoryWatchAdmin/DatePickerInput';
import { useAdminUpdatesService } from '@/services/backoffice';

const UpdateTypeBadge: FC<{ updateType: string }> = ({ updateType }) => {
  const label = UPDATE_TYPE_LABELS[updateType] ?? updateType;
  switch (updateType) {
    case 'new':
      return (
        <Badge className="h-6 gap-1 border-transparent bg-green-100 text-neutral-900 shadow-none">
          <Star className="h-3 w-3" />
          {label}
        </Badge>
      );
    case 'revision':
      return (
        <Badge className="h-6 gap-1 border-transparent bg-yellow-100 text-neutral-900 shadow-none">
          <Copy className="h-3 w-3" />
          {label}
        </Badge>
      );
    case 'withdrawal':
      return (
        <Badge className="h-6 gap-1 border-transparent bg-red-100 text-neutral-900 shadow-none">
          <Ban className="h-3 w-3" />
          {label}
        </Badge>
      );
    case 'status_change':
      return (
        <Badge className="h-6 gap-1 border-transparent bg-blue-100 text-neutral-900 shadow-none">
          <span className="font-bold">&raquo;</span>
          {label}
        </Badge>
      );
    default:
      return (
        <Badge color="purple" className="gap-1">
          {label}
        </Badge>
      );
  }
};

const UpdateTypeBadgeSelect: FC<{
  value: string;
  onChange: (value: string) => void;
}> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className="cursor-pointer"
        >
          <UpdateTypeBadge updateType={value} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0">
        <div className="max-h-[280px] overflow-y-auto py-1">
          {UPDATE_TYPES.filter(t => t !== 'unknown').map(t => (
            <button
              key={t}
              onClick={() => {
                onChange(t);
                setOpen(false);
              }}
              className={cn(
                'flex w-full items-center gap-2 px-3 py-1.5 text-sm',
                'hover:bg-accent focus-visible:outline-none focus-visible:bg-accent'
              )}
            >
              <Check
                className={cn(
                  'h-4 w-4 shrink-0',
                  value === t ? 'opacity-100' : 'opacity-0'
                )}
              />
              <UpdateTypeBadge updateType={t} />
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const NO_VERSION = '__none__';

export const AdminEditUpdatePage: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { updateId } = useParams<{ updateId: string }>();

  const { updates } = useAdminUpdatesService({});
  const update = updates.find(u => u.id === updateId) ?? null;

  const [updateType, setUpdateType] = useState('');
  const [shortSummary, setShortSummary] = useState('');
  const [summary, setSummary] = useState('');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [publicationDate, setPublicationDate] = useState('');
  const [versionId, setVersionId] = useState('');
  const [articleUrl, setArticleUrl] = useState('');
  const [activeTab, setActiveTab] = useState<'previous' | 'redline' | 'latest'>(
    'latest'
  );
  const summaryRef = useRef<HTMLTextAreaElement>(null);
  const [initialized, setInitialized] = useState(false);

  const isNews = update?.kind === 'news';

  const { detail } = useAdminUpdateDetailService(updateId);
  const { versions } = useAdminVersionsService(
    !isNews && update?.referenceId ? update.referenceId : undefined
  );
  const { updateUpdate, isPending } = useAdminUpdateUpdateService();
  const { uploadFile, isPending: isUploading } =
    useAdminUploadUpdateFileService();
  const { deleteFile, isPending: isDeleting } =
    useAdminDeleteUpdateFileService();
  const { generateSummary, isPending: isGeneratingSummary } =
    useAdminGenerateSummaryService();
  const { discardUpdate, isPending: isDiscarding } =
    useAdminDiscardUpdateService();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) setSelectedFile(file);
      e.target.value = '';
    },
    []
  );

  const handleFileUpload = () => {
    if (!update || !selectedFile) return;
    uploadFile(
      { updateId: update.id, file: selectedFile },
      { onSuccess: () => setSelectedFile(null) }
    );
  };

  const handleFileDelete = () => {
    if (!update) return;
    deleteFile(update.id);
  };

  useEffect(() => {
    if (update && !initialized) {
      setUpdateType(update.updateType ?? '');
      setShortSummary(update.shortSummary ?? '');
      setSummary(update.summary ?? '');
      setPublicationDate(
        new Date(update.publicationDate).toISOString().split('T')[0]
      );
      setVersionId(update.versionId ?? NO_VERSION);
      setArticleUrl(update.articleUrl ?? '');
      setTitle(update.title ?? '');
      setContent(update.content ?? '');
      setActiveTab(update.redlineStatus === 'completed' ? 'redline' : 'latest');
      setSelectedFile(null);
      setInitialized(true);
    }
  }, [update, initialized]);

  useEffect(() => {
    setInitialized(false);
  }, [updateId]);

  useEffect(() => {
    const el = summaryRef.current;
    if (!el) return;
    const resize = () => {
      el.style.height = 'auto';
      el.style.height = `${Math.min(Math.max(el.scrollHeight, 120), 300)}px`;
    };
    resize();
    requestAnimationFrame(resize);
  }, [summary]);

  const navigateBack = () => {
    navigate(
      `${ADMIN_PATH}/${adminPaths.regulatoryWatch}/${adminRegulatoryWatchPaths.updates}${location.search}`
    );
  };

  const handleSubmit = () => {
    if (!update) return;
    updateUpdate(
      {
        id: update.id,
        body: {
          ...(isNews
            ? {
                articleUrl: articleUrl || undefined,
                title: title || undefined,
                content: content || undefined,
              }
            : {
                updateType,
                versionId: versionId === NO_VERSION ? null : versionId,
              }),
          shortSummary,
          summary,
          publicationDate,
        },
      },
      {
        onSuccess: () => {
          navigateBack();
        },
      }
    );
  };

  const currentVersion = detail?.currentVersion ?? null;
  const previousVersion = detail?.previousVersion ?? null;
  const updateSharedFile = detail?.sharedFile ?? null;

  const isNewUpdate = update?.updateType === 'new';
  const hasUpdatePdf = !isNews && !!updateSharedFile;
  const showPreviousTab =
    !isNews && !hasUpdatePdf && !isNewUpdate && !!previousVersion;
  const showRedlineTab =
    !isNews &&
    !hasUpdatePdf &&
    !isNewUpdate &&
    !!currentVersion?.sharedFile &&
    !!previousVersion?.sharedFile;

  const displayedVersion =
    activeTab === 'previous' ? previousVersion : currentVersion;

  if (!update) {
    return (
      <FlexCol gap="6" w="full" h="full" p="6" className="max-w-[1600px]">
        <Button variant="ghost" onClick={navigateBack} className="w-fit gap-2">
          <ArrowLeft size={16} />
          Back to Updates
        </Button>
        <p className="text-sm text-muted-foreground">
          {updates.length === 0 ? 'Loading...' : 'Update not found.'}
        </p>
      </FlexCol>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-4 px-6 py-4 border-b bg-white shrink-0">
        <Button variant="ghost" onClick={navigateBack} className="gap-2">
          <ArrowLeft size={16} />
          Back
        </Button>
        <h2 className="text-xl font-semibold flex-1">
          {isNews
            ? (update.title ??
              update.shortSummary ??
              update.sourceTitle ??
              'News Update')
            : update.referenceTitle}
        </h2>
        {isNews && (
          <Button
            variant="outline"
            size="sm"
            className="text-red-600 hover:text-red-700 hover:bg-red-50 shrink-0"
            disabled={isDiscarding}
            onClick={() => {
              if (!window.confirm('Move this news update to discarded items?'))
                return;
              discardUpdate(update.id, {
                onSuccess: () => {
                  toast.success('Update moved to discarded');
                  navigateBack();
                },
                onError: err => {
                  toast.error(err.message || 'Failed to discard update');
                },
              });
            }}
          >
            {isDiscarding ? (
              <Loader2 size={14} className="animate-spin mr-1" />
            ) : (
              <Ban size={14} className="mr-1" />
            )}
            Discard
          </Button>
        )}
      </div>

      <div className="flex-1 min-h-0 flex gap-4 p-4 overflow-hidden bg-neutral-50">
        {/* Left side: Document preview */}
        <div className="flex-1 flex flex-col min-w-0">
          {!isNews && !hasUpdatePdf && currentVersion && (
            <div className="shrink-0 pb-4">
              <div className="flex shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]">
                {showPreviousTab && (
                  <button
                    onClick={() => setActiveTab('previous')}
                    className={cn(
                      'flex-1 flex flex-col items-center justify-center min-h-[40px] px-3 py-2.5 border border-neutral-200 rounded-l-lg -mr-px transition-colors',
                      activeTab === 'previous' ? 'bg-neutral-100' : 'bg-white'
                    )}
                  >
                    <span className="text-sm font-medium text-neutral-900 tracking-[0.07px]">
                      Previous version
                    </span>
                    {previousVersion?.effectiveDate && (
                      <span className="text-xs font-normal text-neutral-600 tracking-[0.18px]">
                        {dayjs(
                          previousVersion.effectiveDate.substring(0, 10)
                        ).format('MMMM D, YYYY')}
                      </span>
                    )}
                  </button>
                )}
                {showRedlineTab && (
                  <button
                    onClick={() => setActiveTab('redline')}
                    className={cn(
                      'flex-1 flex items-center justify-center min-h-[40px] px-3 py-2.5 border border-neutral-200 -mr-px transition-colors',
                      !showPreviousTab && 'rounded-l-lg',
                      activeTab === 'redline' ? 'bg-neutral-100' : 'bg-white'
                    )}
                  >
                    <span className="text-sm font-medium text-neutral-900 tracking-[0.07px]">
                      Updates
                    </span>
                  </button>
                )}
                <button
                  onClick={() => setActiveTab('latest')}
                  className={cn(
                    'flex-1 flex flex-col items-center justify-center min-h-[40px] px-3 py-2.5 border border-neutral-200 transition-colors',
                    !showPreviousTab && !showRedlineTab
                      ? 'rounded-lg'
                      : 'rounded-r-lg',
                    activeTab === 'latest' ? 'bg-neutral-100' : 'bg-white'
                  )}
                >
                  <span className="text-sm font-medium text-neutral-900 tracking-[0.07px]">
                    Updated version
                  </span>
                  {currentVersion?.effectiveDate && (
                    <span className="text-xs font-normal text-neutral-600 tracking-[0.18px]">
                      {dayjs(
                        currentVersion.effectiveDate.substring(0, 10)
                      ).format('MMMM D, YYYY')}
                    </span>
                  )}
                </button>
              </div>
            </div>
          )}

          <div className="flex-1 min-h-0 bg-white border border-neutral-200 rounded-2xl overflow-hidden">
            {hasUpdatePdf ? (
              <div className="relative h-full">
                <iframe
                  src={updateSharedFile!.signedUrl}
                  className="w-full h-full"
                  title={updateSharedFile!.name}
                />
                <div className="absolute top-3 right-3">
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleFileDelete}
                    disabled={isDeleting}
                  >
                    {isDeleting ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4 mr-1" />
                    )}
                    Remove
                  </Button>
                </div>
              </div>
            ) : isNews ? (
              <NewsDocumentPanel
                detail={detail}
                selectedFile={selectedFile}
                fileInputRef={fileInputRef}
                onFileSelect={handleFileSelect}
                onFileDrop={file => setSelectedFile(file)}
                onFileUpload={handleFileUpload}
                onFileDelete={handleFileDelete}
                onClearSelected={() => setSelectedFile(null)}
                isUploading={isUploading}
                isDeleting={isDeleting}
                articleUrl={update.articleUrl}
              />
            ) : activeTab === 'redline' ? (
              <RedlineContent
                detail={detail}
                updateId={update.id}
                canGenerate={showRedlineTab}
              />
            ) : displayedVersion?.sharedFile ? (
              <iframe
                src={displayedVersion.sharedFile.signedUrl}
                className="w-full h-full"
                title={displayedVersion.sharedFile.name}
              />
            ) : !isNews && !currentVersion ? (
              <div className="flex flex-col items-center justify-center h-full gap-6 p-8">
                <div className="text-center text-neutral-500">
                  <FileText
                    size={40}
                    className="mx-auto mb-3 text-neutral-300"
                  />
                  <p className="text-sm font-medium">
                    No version linked to this update
                  </p>
                  <p className="text-xs text-neutral-400 mt-1">
                    Upload a PDF as the update document
                  </p>
                </div>
                {selectedFile ? (
                  <div className="flex flex-col items-center gap-3">
                    <div className="flex items-center gap-2 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm">
                      <FileText
                        size={16}
                        className="text-neutral-500 shrink-0"
                      />
                      <span className="truncate max-w-[200px]">
                        {selectedFile.name}
                      </span>
                      <button
                        onClick={() => setSelectedFile(null)}
                        className="text-neutral-400 hover:text-neutral-600"
                      >
                        <X size={14} />
                      </button>
                    </div>
                    <Button
                      size="sm"
                      onClick={handleFileUpload}
                      disabled={isUploading}
                    >
                      {isUploading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          Upload PDF
                        </>
                      )}
                    </Button>
                  </div>
                ) : (
                  <div
                    className="w-full max-w-md border-2 border-dashed border-neutral-300 rounded-xl p-8 text-center cursor-pointer hover:border-neutral-400 hover:bg-neutral-50 transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={e => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onDrop={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      const file = e.dataTransfer.files[0];
                      if (file?.type === 'application/pdf')
                        setSelectedFile(file);
                    }}
                  >
                    <Upload
                      size={24}
                      className="mx-auto mb-2 text-neutral-400"
                    />
                    <p className="text-sm text-neutral-600">
                      Drop a PDF here or click to browse
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,application/pdf"
                      className="hidden"
                      onChange={e => {
                        const file = e.target.files?.[0];
                        if (file) setSelectedFile(file);
                        e.target.value = '';
                      }}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full gap-3 text-neutral-500">
                <FileText size={48} className="text-neutral-300" />
                <p className="text-sm">No document uploaded for this version</p>
              </div>
            )}
          </div>
        </div>

        {/* Right side: Update information */}
        <div className="w-[480px] shrink-0 bg-white border border-neutral-200 rounded-2xl shadow-lg flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {isNews ? (
              <>
                <div className="space-y-2">
                  <span className="text-[11px] font-medium text-neutral-600 uppercase tracking-[1px]">
                    Source
                  </span>
                  <div className="text-sm font-medium">
                    {update.sourceTitle ?? '-'}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Title</Label>
                  <Input
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    placeholder="Full article title..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Article URL</Label>
                  <Input
                    value={articleUrl}
                    onChange={e => setArticleUrl(e.target.value)}
                    placeholder="https://..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Publication Date</Label>
                  <DatePickerInput
                    value={publicationDate}
                    onChange={setPublicationDate}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <span className="text-[11px] font-medium text-neutral-600 uppercase tracking-[1px]">
                    Reference
                  </span>
                  <div className="text-sm font-medium">
                    {update.referenceTitle}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Update Type</Label>
                    <div>
                      <UpdateTypeBadgeSelect
                        value={updateType}
                        onChange={setUpdateType}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Publication Date</Label>
                    <DatePickerInput
                      value={publicationDate}
                      onChange={setPublicationDate}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Updated Version</Label>
                  <Select value={versionId} onValueChange={setVersionId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select version" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_VERSION}>None</SelectItem>
                      {versions.map(v => (
                        <SelectItem key={v.id} value={v.id}>
                          {v.effectiveDate
                            ? dayjs(v.effectiveDate.substring(0, 10)).format(
                                'D MMMM YYYY'
                              )
                            : 'No date'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {currentVersion?.effectiveDate && (
                  <div className="space-y-2">
                    <span className="text-[11px] font-medium text-neutral-600 uppercase tracking-[1px]">
                      Version Effective Date
                    </span>
                    <div className="text-sm">
                      {dayjs(
                        currentVersion.effectiveDate.substring(0, 10)
                      ).format('D MMMM YYYY')}
                    </div>
                  </div>
                )}
                {currentVersion?.url && (
                  <div className="space-y-2">
                    <span className="text-[11px] font-medium text-neutral-600 uppercase tracking-[1px]">
                      Version URL
                    </span>
                    <a
                      href={currentVersion.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-sm text-blue-600 hover:underline truncate"
                    >
                      {currentVersion.url}
                    </a>
                  </div>
                )}
                {detail?.referenceUrl && (
                  <div className="space-y-2">
                    <span className="text-[11px] font-medium text-neutral-600 uppercase tracking-[1px]">
                      Reference URL
                    </span>
                    <a
                      href={detail.referenceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-sm text-blue-600 hover:underline truncate"
                    >
                      {detail.referenceUrl}
                    </a>
                  </div>
                )}
              </>
            )}

            <div className="h-px w-full bg-neutral-200" />

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Short Summary</Label>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 gap-1.5 text-xs"
                  onClick={() => {
                    if (!update) return;
                    generateSummary(update.id, {
                      onSuccess: data => {
                        if (data.shortSummary != null)
                          setShortSummary(data.shortSummary);
                        if (data.summary != null) setSummary(data.summary);
                      },
                      onError: err => {
                        toast.error(
                          err instanceof Error
                            ? err.message
                            : 'Failed to generate summary'
                        );
                      },
                    });
                  }}
                  disabled={isGeneratingSummary}
                >
                  {isGeneratingSummary ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <Sparkles size={12} />
                  )}
                  {isGeneratingSummary ? 'Generating...' : 'Generate'}
                </Button>
              </div>
              <Input
                value={shortSummary}
                onChange={e => setShortSummary(e.target.value)}
                placeholder="Short summary of the update"
              />
            </div>

            <div className="space-y-2">
              <Label>Summary (markdown)</Label>
              <Textarea
                ref={summaryRef}
                value={summary}
                onChange={e => setSummary(e.target.value)}
                className="min-h-[120px] resize-y overflow-y-auto font-mono text-xs"
                placeholder="Enter update summary..."
              />
              {summary && (
                <div className="rounded-md border border-neutral-200 bg-neutral-50 p-3 min-h-[80px] max-h-[400px] overflow-y-auto resize-y">
                  <MarkdownText size="sm">{summary}</MarkdownText>
                </div>
              )}
            </div>

            {isNews && (
              <div className="space-y-2">
                <Label>Content</Label>
                <Textarea
                  value={content}
                  onChange={e => setContent(e.target.value)}
                  className="min-h-[200px] resize-none"
                  placeholder="Full article content..."
                />
              </div>
            )}
          </div>

          <div className="shrink-0 flex justify-end gap-2 px-6 py-4 border-t">
            <Button variant="outline" onClick={navigateBack}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isPending}>
              {isPending ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const NewsDocumentPanel: FC<{
  detail: AdminUpdateDetailResponse | null;
  selectedFile: File | null;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFileDrop: (file: File) => void;
  onFileUpload: () => void;
  onFileDelete: () => void;
  onClearSelected: () => void;
  isUploading: boolean;
  isDeleting: boolean;
  articleUrl?: string | null;
}> = ({
  detail,
  selectedFile,
  fileInputRef,
  onFileSelect,
  onFileDrop,
  onFileUpload,
  onFileDelete,
  onClearSelected,
  isUploading,
  isDeleting,
  articleUrl,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const dragCounterRef = useRef(0);
  const existingFile = detail?.sharedFile ?? null;

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.types.includes('Files')) setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      dragCounterRef.current = 0;
      const file = e.dataTransfer.files?.[0];
      if (file && file.type === 'application/pdf') onFileDrop(file);
    },
    [onFileDrop]
  );

  const dropProps = {
    onDragEnter: handleDragEnter,
    onDragLeave: handleDragLeave,
    onDragOver: handleDragOver,
    onDrop: handleDrop,
  };

  if (existingFile && !selectedFile) {
    return (
      <div className="flex flex-col h-full relative" {...dropProps}>
        {isDragging && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-blue-50/80 border-2 border-dashed border-blue-400 rounded-2xl">
            <p className="text-sm font-medium text-blue-600">
              Drop to replace file
            </p>
          </div>
        )}
        <div className="flex-1 min-h-0">
          <iframe
            src={existingFile.signedUrl}
            className="w-full h-full"
            title={existingFile.name}
          />
        </div>
        <div className="shrink-0 flex items-center justify-between px-4 py-3 border-t">
          <div className="flex items-center gap-2 min-w-0">
            <FileText size={16} className="text-neutral-500 shrink-0" />
            <span className="text-sm text-neutral-700 truncate">
              {existingFile.name}
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
            >
              Replace
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onFileDelete}
              disabled={isDeleting}
              className="text-red-600 hover:text-red-700"
            >
              {isDeleting ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Trash2 size={14} />
              )}
            </Button>
          </div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          className="hidden"
          onChange={onFileSelect}
        />
      </div>
    );
  }

  if (selectedFile) {
    return (
      <div
        className="flex flex-col items-center justify-center h-full gap-4 text-neutral-500"
        {...dropProps}
      >
        {isDragging && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-blue-50/80 border-2 border-dashed border-blue-400 rounded-2xl">
            <p className="text-sm font-medium text-blue-600">
              Drop to replace file
            </p>
          </div>
        )}
        <FileText size={48} className="text-neutral-300" />
        <div className="text-center">
          <p className="text-sm font-medium text-neutral-700">
            {selectedFile.name}
          </p>
          <p className="text-xs mt-1">
            {(selectedFile.size / 1024 / 1024).toFixed(1)} MB
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onClearSelected}>
            <X size={14} className="mr-1" />
            Cancel
          </Button>
          <Button size="sm" onClick={onFileUpload} disabled={isUploading}>
            {isUploading ? (
              <Loader2 size={14} className="mr-1 animate-spin" />
            ) : (
              <Upload size={14} className="mr-1" />
            )}
            Upload
          </Button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          className="hidden"
          onChange={onFileSelect}
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center h-full gap-3 text-neutral-500 transition-colors',
        isDragging &&
          'bg-blue-50/80 border-2 border-dashed border-blue-400 rounded-2xl'
      )}
      {...dropProps}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf"
        className="hidden"
        onChange={onFileSelect}
      />
      <button
        onClick={() => fileInputRef.current?.click()}
        className="flex flex-col items-center gap-3 cursor-pointer hover:text-neutral-700 transition-colors"
      >
        <Upload
          size={48}
          className={isDragging ? 'text-blue-400' : 'text-neutral-300'}
        />
        <p className="text-sm">
          {isDragging
            ? 'Drop PDF here'
            : 'Drag & drop or click to attach a PDF'}
        </p>
      </button>
      {articleUrl && (
        <a
          href={articleUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-blue-600 hover:underline"
        >
          View original article
        </a>
      )}
    </div>
  );
};

const RedlineContent: FC<{
  detail: AdminUpdateDetailResponse | null;
  updateId: string;
  canGenerate: boolean;
}> = ({ detail, updateId, canGenerate }) => {
  const { generateRedline, isPending } = useAdminGenerateRedlineService();
  const redlineStatus = detail?.redline.status ?? 'none';
  const redlineExcelUrl = detail?.redline.excelFileUrl ?? null;

  if (redlineStatus === 'completed' && redlineExcelUrl) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 min-h-0">
          <iframe
            src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(redlineExcelUrl)}`}
            className="w-full h-full border-0"
            title="Redline Excel preview"
          />
        </div>
        <div className="shrink-0 flex justify-center gap-3 py-3 border-t">
          <a href={redlineExcelUrl} download="redline.xlsx">
            <Button variant="outline" size="sm" className="gap-2">
              <Download size={14} />
              Download Excel
            </Button>
          </a>
          <Button
            variant="outline"
            size="sm"
            onClick={() => generateRedline(updateId)}
            disabled={isPending}
          >
            <RefreshCw
              size={14}
              className={cn('mr-1.5', isPending && 'animate-spin')}
            />
            Regenerate
          </Button>
        </div>
      </div>
    );
  }

  if (redlineStatus === 'generating') {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 text-neutral-500">
        <Loader2 size={48} className="text-neutral-400 animate-spin" />
        <p className="text-sm">Generating redline...</p>
        <p className="text-xs text-neutral-400">
          This may take several minutes for large documents.
        </p>
      </div>
    );
  }

  if (redlineStatus === 'failed') {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 text-neutral-500">
        <AlertCircle size={48} className="text-red-400" />
        <p className="text-sm">Redline generation failed.</p>
        {canGenerate && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => generateRedline(updateId)}
            disabled={isPending}
          >
            <RefreshCw
              size={14}
              className={cn('mr-1.5', isPending && 'animate-spin')}
            />
            Retry
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center h-full gap-3 text-neutral-500">
      <FileText size={48} className="text-neutral-300" />
      <p className="text-sm">No redline has been generated for this update.</p>
      {canGenerate && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => generateRedline(updateId)}
          disabled={isPending}
        >
          {isPending ? (
            <Loader2 size={14} className="mr-1.5 animate-spin" />
          ) : null}
          Generate redline
        </Button>
      )}
    </div>
  );
};
